import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, Upload, X, Plus, Trash2, Image as ImageIcon, 
  ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const cacheBust = `?_t=${Date.now()}`;
      const response = await axios.get(`${API}/content${cacheBust}`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const savePageContent = async (page) => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/content/page/${page}`, {
        page,
        content: content[page]
      });
      toast.success(`${page.charAt(0).toUpperCase() + page.slice(1)} page saved!`);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  const resetContent = async () => {
    if (!window.confirm('Are you sure you want to reset all content to defaults? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/content/reset`);
      await fetchContent();
      toast.success('Content reset to defaults');
    } catch (error) {
      toast.error('Failed to reset content');
    }
  };

  const updateField = (page, field, value) => {
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [field]: value
      }
    }));
  };

  const updateNestedField = (page, index, field, value) => {
    setContent(prev => {
      const newArray = [...prev[page]];
      newArray[index] = { ...newArray[index], [field]: value };
      return { ...prev, [page]: newArray };
    });
  };

  const updateArrayItem = (page, parentField, index, field, value) => {
    setContent(prev => {
      const newArray = [...prev[page][parentField]];
      if (typeof newArray[index] === 'object') {
        newArray[index] = { ...newArray[index], [field]: value };
      } else {
        newArray[index] = value;
      }
      return {
        ...prev,
        [page]: { ...prev[page], [parentField]: newArray }
      };
    });
  };

  const addArrayItem = (page, parentField, template) => {
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [parentField]: [...(prev[page][parentField] || []), template]
      }
    }));
  };

  const removeArrayItem = (page, parentField, index) => {
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [parentField]: prev[page][parentField].filter((_, i) => i !== index)
      }
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await axios.post(`${API}/upload-image`, {
          page: uploadTarget.page,
          field: uploadTarget.field,
          image_data: reader.result
        });
        
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`;
        
        if (uploadTarget.index !== undefined) {
          if (uploadTarget.parentField) {
            updateArrayItem(uploadTarget.page, uploadTarget.parentField, uploadTarget.index, uploadTarget.field, imageUrl);
          } else {
            updateNestedField(uploadTarget.page, uploadTarget.index, uploadTarget.field, imageUrl);
          }
        } else {
          updateField(uploadTarget.page, uploadTarget.field, imageUrl);
        }
        
        toast.success('Image uploaded!');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload image');
      }
    };
    reader.readAsDataURL(file);
    setUploadTarget(null);
  };

  const triggerImageUpload = (page, field, index, parentField) => {
    setUploadTarget({ page, field, index, parentField });
    fileInputRef.current?.click();
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-12 w-12 border-4 border-[#C9A86C] border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Loading content...</p>
      </div>
    );
  }

  if (!content) return null;

  const tabs = [
    { id: 'home', label: 'Home Page' },
    { id: 'about', label: 'About Page' },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact Info' }
  ];

  return (
    <div className="p-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1412]">Content Editor</h2>
        <button
          onClick={resetContent}
          className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
        >
          <RefreshCw size={16} />
          Reset to Defaults
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-[#C9A86C] border-[#C9A86C]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* HOME PAGE */}
        {activeTab === 'home' && (
          <div className="space-y-6">
            <Section title="Hero Section" expanded={expandedSections.homeHero !== false} onToggle={() => toggleSection('homeHero')}>
              <ImageField
                label="Logo"
                value={content.home?.logo_url}
                onChange={(v) => updateField('home', 'logo_url', v)}
                onUpload={() => triggerImageUpload('home', 'logo_url')}
              />
              <TextField label="Headline" value={content.home?.headline} onChange={(v) => updateField('home', 'headline', v)} />
              <TextField label="Motto" value={content.home?.motto} onChange={(v) => updateField('home', 'motto', v)} />
              <TextArea label="Description" value={content.home?.description} onChange={(v) => updateField('home', 'description', v)} />
              <TextField label="Primary Button Text" value={content.home?.cta_primary} onChange={(v) => updateField('home', 'cta_primary', v)} />
              <TextField label="Secondary Button Text" value={content.home?.cta_secondary} onChange={(v) => updateField('home', 'cta_secondary', v)} />
            </Section>

            <Section title="Statistics" expanded={expandedSections.homeStats !== false} onToggle={() => toggleSection('homeStats')}>
              {content.home?.stats?.map((stat, index) => (
                <div key={index} className="flex gap-4 items-end mb-4 p-4 bg-gray-50 rounded-lg">
                  <TextField label="Value" value={stat.value} onChange={(v) => updateArrayItem('home', 'stats', index, 'value', v)} className="flex-1" />
                  <TextField label="Label" value={stat.label} onChange={(v) => updateArrayItem('home', 'stats', index, 'label', v)} className="flex-1" />
                  <button onClick={() => removeArrayItem('home', 'stats', index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('home', 'stats', { value: '', label: '' })}
                className="flex items-center gap-2 text-[#C9A86C] hover:underline"
              >
                <Plus size={16} /> Add Statistic
              </button>
            </Section>

            <SaveButton onClick={() => savePageContent('home')} isSaving={isSaving} />
          </div>
        )}

        {/* ABOUT PAGE */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            <Section title="Profile" expanded={expandedSections.aboutProfile !== false} onToggle={() => toggleSection('aboutProfile')}>
              <ImageField
                label="Photo"
                value={content.about?.photo_url}
                onChange={(v) => updateField('about', 'photo_url', v)}
                onUpload={() => triggerImageUpload('about', 'photo_url')}
              />
              <TextField label="Name" value={content.about?.name} onChange={(v) => updateField('about', 'name', v)} />
              <TextField label="Title" value={content.about?.title} onChange={(v) => updateField('about', 'title', v)} />
              <TextArea label="Biography" value={content.about?.bio} onChange={(v) => updateField('about', 'bio', v)} rows={8} />
            </Section>

            <Section title="Credentials" expanded={expandedSections.aboutCreds !== false} onToggle={() => toggleSection('aboutCreds')}>
              {content.about?.credentials?.map((cred, index) => (
                <div key={index} className="flex gap-4 items-end mb-4 p-4 bg-gray-50 rounded-lg">
                  <TextField label="Degree" value={cred.degree} onChange={(v) => updateArrayItem('about', 'credentials', index, 'degree', v)} className="flex-1" />
                  <TextField label="Institution" value={cred.institution} onChange={(v) => updateArrayItem('about', 'credentials', index, 'institution', v)} className="flex-1" />
                  <button onClick={() => removeArrayItem('about', 'credentials', index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('about', 'credentials', { degree: '', institution: '' })}
                className="flex items-center gap-2 text-[#C9A86C] hover:underline"
              >
                <Plus size={16} /> Add Credential
              </button>
            </Section>

            <Section title="Experience Areas" expanded={expandedSections.aboutExp !== false} onToggle={() => toggleSection('aboutExp')}>
              <div className="flex flex-wrap gap-2 mb-4">
                {content.about?.experience_areas?.map((area, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => {
                        const newAreas = [...content.about.experience_areas];
                        newAreas[index] = e.target.value;
                        updateField('about', 'experience_areas', newAreas);
                      }}
                      className="bg-transparent border-none text-sm w-24 focus:outline-none"
                    />
                    <button onClick={() => removeArrayItem('about', 'experience_areas', index)} className="text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => updateField('about', 'experience_areas', [...(content.about?.experience_areas || []), ''])}
                className="flex items-center gap-2 text-[#C9A86C] hover:underline"
              >
                <Plus size={16} /> Add Area
              </button>
            </Section>

            <Section title="Affiliations" expanded={expandedSections.aboutAff !== false} onToggle={() => toggleSection('aboutAff')}>
              {content.about?.affiliations?.map((aff, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={aff}
                    onChange={(e) => {
                      const newAffs = [...content.about.affiliations];
                      newAffs[index] = e.target.value;
                      updateField('about', 'affiliations', newAffs);
                    }}
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  <button onClick={() => removeArrayItem('about', 'affiliations', index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => updateField('about', 'affiliations', [...(content.about?.affiliations || []), ''])}
                className="flex items-center gap-2 text-[#C9A86C] hover:underline"
              >
                <Plus size={16} /> Add Affiliation
              </button>
            </Section>

            <Section title="Current Roles" expanded={expandedSections.aboutRoles !== false} onToggle={() => toggleSection('aboutRoles')}>
              <TextArea label="Current Roles Description" value={content.about?.current_roles} onChange={(v) => updateField('about', 'current_roles', v)} rows={3} />
            </Section>

            <SaveButton onClick={() => savePageContent('about')} isSaving={isSaving} />
          </div>
        )}

        {/* SERVICES PAGE */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {content.services?.map((service, index) => (
              <Section
                key={index}
                title={service.title || `Service ${index + 1}`}
                expanded={expandedSections[`service${index}`] !== false}
                onToggle={() => toggleSection(`service${index}`)}
              >
                <TextField label="Title" value={service.title} onChange={(v) => updateNestedField('services', index, 'title', v)} />
                <TextArea label="Description" value={service.description} onChange={(v) => updateNestedField('services', index, 'description', v)} />
                <ImageField
                  label="Image"
                  value={service.image_url}
                  onChange={(v) => updateNestedField('services', index, 'image_url', v)}
                  onUpload={() => triggerImageUpload('services', 'image_url', index)}
                />
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                  {service.features?.map((feature, fIndex) => (
                    <div key={fIndex} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...service.features];
                          newFeatures[fIndex] = e.target.value;
                          updateNestedField('services', index, 'features', newFeatures);
                        }}
                        className="flex-1 px-3 py-2 border rounded"
                      />
                      <button
                        onClick={() => {
                          const newFeatures = service.features.filter((_, i) => i !== fIndex);
                          updateNestedField('services', index, 'features', newFeatures);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newFeatures = [...(service.features || []), ''];
                      updateNestedField('services', index, 'features', newFeatures);
                    }}
                    className="flex items-center gap-2 text-[#C9A86C] hover:underline"
                  >
                    <Plus size={16} /> Add Feature
                  </button>
                </div>
              </Section>
            ))}
            <SaveButton onClick={() => savePageContent('services')} isSaving={isSaving} />
          </div>
        )}

        {/* CONTACT INFO */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <Section title="Contact Information" expanded={true}>
              <TextField label="Phone" value={content.contact?.phone} onChange={(v) => updateField('contact', 'phone', v)} />
              <TextField label="Email" value={content.contact?.email} onChange={(v) => updateField('contact', 'email', v)} />
              <TextField label="Address" value={content.contact?.address} onChange={(v) => updateField('contact', 'address', v)} />
              <TextField label="LinkedIn URL" value={content.contact?.linkedin} onChange={(v) => updateField('contact', 'linkedin', v)} />
              <TextField label="Office Hours" value={content.contact?.office_hours} onChange={(v) => updateField('contact', 'office_hours', v)} />
              <TextField label="Office Hours Note" value={content.contact?.office_hours_note} onChange={(v) => updateField('contact', 'office_hours_note', v)} />
            </Section>
            <SaveButton onClick={() => savePageContent('contact')} isSaving={isSaving} />
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const Section = ({ title, children, expanded, onToggle }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
    >
      <h3 className="font-heading font-semibold text-[#1a1412]">{title}</h3>
      {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </button>
    {expanded && <div className="p-4 pt-0 space-y-4">{children}</div>}
  </div>
);

const TextField = ({ label, value, onChange, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A86C] focus:border-transparent"
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 4 }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A86C] focus:border-transparent resize-none"
    />
  </div>
);

const ImageField = ({ label, value, onChange, onUpload }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex gap-3">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Image URL"
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A86C] focus:border-transparent"
      />
      <button
        onClick={onUpload}
        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        <Upload size={18} />
        Upload
      </button>
    </div>
    {value && (
      <div className="mt-2">
        <img src={value} alt={label} className="h-20 w-auto rounded border" />
      </div>
    )}
  </div>
);

const SaveButton = ({ onClick, isSaving }) => (
  <button
    onClick={onClick}
    disabled={isSaving}
    className="flex items-center gap-2 px-6 py-3 bg-[#C9A86C] hover:bg-[#b8975b] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
  >
    {isSaving ? (
      <>
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        Saving...
      </>
    ) : (
      <>
        <Save size={18} />
        Save Changes
      </>
    )}
  </button>
);

export default ContentEditor;
